import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Card, CardContent } from '@core-ui/Components/ui/card';

import useVoicemailApi from '@features/voicemail/hooks/useVoicemailApi';

import WizardStepper from '../components/WizardStepper';
import WizardNavigation from '../components/WizardNavigation';
import SelectIntentStep from '../components/steps/SelectIntentStep';
import SelectContactsStep from '../components/steps/SelectContactsStep';
import SelectVoicemailStep from '../components/steps/SelectVoicemailStep';
import SessionSettingsStep from '../components/steps/SessionSettingsStep';
import SummaryStep from '../components/steps/SummaryStep';
import ActiveSessionPrompt from '../components/ActiveSessionPrompt';
import SavedProgressPrompt from '../components/SavedProgressPrompt';
import useWizardStore from '../store/useWizardStore';
import useWizardApi from '../hooks/useWizardApi';
import type { WizardStep, CallSessionResponse } from '@features/wizard';
import MicrophoneSetupModal from '@ui/shared/Dialer/MicrophoneSetupModal.tsx';
import { useBreadcrumbStore } from '@ui/Layout/store/useBreadcrumbStore.ts';
import { type ContactsTab } from '@features/contacts';

export default function WizardPage (): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    setCurrentContactsTab,
    currentStep,
    setStep,
    nextStep,
    prevStep,
    goToSummary,
    canProceed,
    reset,
    wizardResetInitialState,
    selectedIntent,
    selectedContactList,
    selectedVoicemail,
    shuffleContacts,
    salesforceNonce,
    ongoingCallAction,
    isEditingFromSummary,
    setIsEditingFromSummary,
    outreachData,
    hubspotNonce,
    stepMode,
  } = useWizardStore();

  const { createSession, checkActiveSession, endSession, isLoading, isCheckingSession } = useWizardApi();
  const { getVoicemailByIdAsync } = useVoicemailApi();

  // Ref for inline-create panels to register their save handler
  const createAndContinueRef = useRef<(() => void) | null>(null);
  const [canCreate, setCanCreate] = useState(false);

  const handleCreateHandlerChange = useCallback((handler: (() => void) | null) => {
    createAndContinueRef.current = handler;
  }, []);

  const handleCanCreateChange = useCallback((value: boolean) => {
    setCanCreate(value);
  }, []);

  const [error, setError] = useState<string | null>(null);
  const [activeSession, setActiveSession] = useState<CallSessionResponse | null>(null);
  const [isEndingSession, setIsEndingSession] = useState(false);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [showSavedProgressPrompt, setShowSavedProgressPrompt] = useState(false);
  const [savedProgressChecked, setSavedProgressChecked] = useState(false);

  const setItems = useBreadcrumbStore((s) => s.setItems);
  useEffect(() => {
    setItems([
      { labelKey: 'breadcrumb.home', href: '/workspace/sessions/new' },
      { labelKey: 'breadcrumb.wizard', href: '/workspace/sessions/new' },
      { labelKey: 'breadcrumb.newSession', href: '/workspace/sessions/new' },
    ]);
  }, [setItems]);

  // Check for active sessions on mount
  useEffect(() => {
    const checkForActiveSession = async (): Promise<void> => {
      const result = await checkActiveSession();
      if (result.hasActiveSession && result.activeSession !== undefined) {
        const session = result.activeSession;
        let enrichedSession = { ...session };

        // Fetch voicemail title if we have voiceDropsIds
        if (session.voiceDropsIds !== undefined && session.voiceDropsIds.length > 0 && session.voicemailTitle === undefined) {
          const vmResult = await getVoicemailByIdAsync(session.voiceDropsIds[0]);
          if (vmResult.success && vmResult.data !== undefined) {
            enrichedSession = { ...enrichedSession, voicemailTitle: vmResult.data.title };
          }
        }

        // Fetch contact list title if we have contactList
        if (session.contactList !== undefined && session.contactList.length > 0 && session.contactListTitle === undefined) {
          if (session.contactList[0].title !== undefined) {
            enrichedSession = { ...enrichedSession, contactListTitle: session.contactList[0].title };
          }
        }

        setActiveSession(enrichedSession);
      }
      setInitialCheckDone(true);
    };

    void checkForActiveSession();
  }, [checkActiveSession, getVoicemailByIdAsync]);

  // Check for saved progress when there's no active session
  useEffect(() => {
    if (initialCheckDone && activeSession === null && !savedProgressChecked) {
      const hasSavedProgress = selectedIntent !== null || selectedContactList !== null || selectedVoicemail !== null;
      if (hasSavedProgress) {
        setShowSavedProgressPrompt(true);
      }
      setSavedProgressChecked(true);
    }
  }, [initialCheckDone, activeSession, savedProgressChecked, selectedIntent, selectedContactList, selectedVoicemail]);

  const handleContinueProgress = useCallback((): void => {
    setShowSavedProgressPrompt(false);
  }, []);

  const handleStartFresh = useCallback((): void => {
    wizardResetInitialState();
    setShowSavedProgressPrompt(false);
  }, [wizardResetInitialState]);

  const handleResumeSession = useCallback((): void => {
    if (activeSession !== null) {
      navigate(`/sessions/${activeSession.id}`);
    }
  }, [activeSession, navigate]);

  const handleEndAndCreate = useCallback(async (): Promise<void> => {
    if (activeSession === null) {
      return;
    }

    setIsEndingSession(true);
    const result = await endSession(activeSession.id);

    if (result.success) {
      setActiveSession(null);
      reset();
    } else {
      setError(result.error ?? t('wizard.errors.endSessionFailed'));
    }

    setIsEndingSession(false);
  }, [activeSession, endSession, reset, t]);

  const handleStepClick = (step: WizardStep): void => {
    if (step < currentStep) {
      setStep(step);
    }
  };

  const handleEditStep = (step: WizardStep): void => {
    setIsEditingFromSummary(true);
    setStep(step);
  };

  const handleSubmit = async (): Promise<void> => {
    if (selectedIntent === null || selectedContactList === null || selectedVoicemail === null) {
      setError(t('wizard.errors.missingFields'));
      return;
    }

    const listType = selectedContactList.type?.toLowerCase() as ContactsTab;
    setCurrentContactsTab(listType);

    setError(null);

    const result = await createSession({
      scriptId: selectedIntent.id ?? '',
      scriptTitle: selectedIntent.title,
      speech: selectedIntent.body,
      contactListId: selectedContactList.id,
      voicemailId: selectedVoicemail.id ?? selectedVoicemail._id ?? '',
      voicemailUrl: selectedVoicemail.recordingUrl ?? selectedVoicemail.url,
      currentContactsTab: listType,
      contactListName: selectedContactList.title,
      shuffleContacts,
      ongoingCallAction,
      salesforceNonce,
      outreachData,
      hubspotNonce
    });

    if (result.success && result.sessionId !== undefined) {
      reset();

      navigate(`/sessions/${result.sessionId}`);
    } else {
      setError(result.error ?? t('wizard.errors.createFailed'));
    }
  };

  const renderStep = (): React.JSX.Element => {
    switch (currentStep) {
    case 1:
      return (
        <SelectIntentStep
          onCreateHandlerChange={handleCreateHandlerChange}
          onCanCreateChange={handleCanCreateChange}
        />
      );
    case 2:
      return <SelectContactsStep />;
    case 3:
      return (
        <SelectVoicemailStep
          onCreateHandlerChange={handleCreateHandlerChange}
          onCanCreateChange={handleCanCreateChange}
        />
      );
    case 4:
      return <SessionSettingsStep />;
    case 5:
      return <SummaryStep onEditStep={handleEditStep} />;
    default:
      return <SelectIntentStep />;
    }
  };

  // Show loading while checking for active sessions
  if (!initialCheckDone || isCheckingSession) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Show active session prompt if there's an active session
  if (activeSession !== null) {
    return (
      <ActiveSessionPrompt
        activeSession={activeSession}
        onResume={handleResumeSession}
        onEnd={() => {
          void handleEndAndCreate();
        }}
        isEnding={isEndingSession}
      />
    );
  }

  // Show saved progress prompt if there's saved data
  if (showSavedProgressPrompt) {
    return (
      <SavedProgressPrompt
        savedIntent={selectedIntent}
        savedContactList={selectedContactList}
        savedVoicemail={selectedVoicemail}
        shuffleContacts={shuffleContacts}
        ongoingCallAction={ongoingCallAction}
        onContinue={handleContinueProgress}
        onStartFresh={handleStartFresh}
      />
    );
  }

  return (
    <div className="relative">
      <MicrophoneSetupModal />
      <div className="mx-auto max-w-4xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">{t('wizard.title')}</h1>
          <p className="text-muted-foreground">{t('wizard.description')}</p>
        </div>

        <WizardStepper currentStep={currentStep} onStepClick={handleStepClick} />

        <Card className="mt-6 p-6 rounded-2xl shadow-lg border border-border bg-card">
          <CardContent className="p-0">
            {renderStep()}

            {error !== null && (
              <div className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <WizardNavigation
              currentStep={currentStep}
              canProceed={canProceed()}
              isLoading={isLoading}
              isEditingFromSummary={isEditingFromSummary}
              stepMode={stepMode[currentStep] ?? 'select'}
              canCreate={canCreate}
              onPrevious={prevStep}
              onNext={nextStep}
              onSubmit={() => {
                void handleSubmit();
              }}
              onBackToSummary={goToSummary}
              onCreateAndContinue={() => {
                createAndContinueRef.current?.();
              }}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
